/**
 * YoutubeHighlight — narzędzie do wycinania highlight'ów z filmów YouTube.
 * Przepływ: podaj URL → AI analizuje → wybiera fragment 2-10 min → pobierz klip.
 */
import { useState } from "react";
import {
  Youtube,
  Scissors,
  Download,
  Loader2,
  Sparkles,
  AlertCircle,
  Clock,
  RefreshCw,
  ExternalLink,
  Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiRequest, getAuthToken } from "@/lib/queryClient";
import type { AnalyzeResponse, HighlightSuggestion } from "./types";

type ViewState = "idle" | "analyzing" | "list" | "editing" | "downloading" | "error";

function extractVideoId(url: string): string | null {
  const match = url.match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

function formatSeconds(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function parseDurationInput(value: string): number {
  const parts = value.split(":").map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parseInt(value) || 0;
}

function durationLabel(startSeconds: number, endSeconds: number): string {
  const diff = endSeconds - startSeconds;
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  return s > 0 ? `${m} min ${s} s` : `${m} min`;
}

export default function YoutubeHighlight() {
  const [url, setUrl] = useState("");
  const [viewState, setViewState] = useState<ViewState>("idle");
  const [videoTitle, setVideoTitle] = useState("");
  const [highlights, setHighlights] = useState<HighlightSuggestion[]>([]);
  const [suggestion, setSuggestion] = useState<HighlightSuggestion | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [showFullExcerpt, setShowFullExcerpt] = useState(false);

  const handleAnalyze = async () => {
    if (!url.trim()) return;
    setViewState("analyzing");
    setErrorMessage("");

    try {
      const data: AnalyzeResponse = await apiRequest(
        "POST",
        "/api/youtube-highlight/analyze",
        { youtubeUrl: url.trim() },
        { signal: AbortSignal.timeout(180_000) }
      );
      setVideoTitle(data.videoTitle);
      setHighlights(data.highlights);
      setViewState("list");
    } catch (err: any) {
      setErrorMessage(err.message ?? "Błąd analizy — sprawdź link i spróbuj ponownie.");
      setViewState("error");
    }
  };

  const handlePickHighlight = (h: HighlightSuggestion) => {
    setSuggestion(h);
    setEditTitle(h.suggestedTitle);
    setEditStart(formatSeconds(h.startSeconds));
    setEditEnd(formatSeconds(h.endSeconds));
    setShowFullExcerpt(false);
    setViewState("editing");
  };

  const handleBackToList = () => {
    setSuggestion(null);
    setViewState("list");
  };

  const handleDownload = async () => {
    if (!suggestion) return;
    setViewState("downloading");

    const startSeconds = parseDurationInput(editStart);
    const endSeconds = parseDurationInput(editEnd);

    try {
      const token = getAuthToken();
      const response = await fetch("/api/youtube-highlight/download", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          youtubeUrl: url.trim(),
          startSeconds,
          endSeconds,
          title: editTitle.trim() || suggestion.suggestedTitle,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: "Błąd pobierania" }));
        throw new Error(err.message ?? "Błąd pobierania");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${(editTitle || "highlight").replace(/[<>:"/\\|?*]/g, "").trim()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
      setViewState("editing");
    } catch {
      setDownloadFailed(true);
      setViewState("editing");
    }
  };

  const handleReset = () => {
    setViewState("idle");
    setHighlights([]);
    setVideoTitle("");
    setSuggestion(null);
    setEditTitle("");
    setEditStart("");
    setEditEnd("");
    setErrorMessage("");
    setDownloadFailed(false);
  };

  const handleBackToListFromEdit = () => {
    setDownloadFailed(false);
    handleBackToList();
  };

  return (
    <div className="flex flex-col items-center justify-start h-full p-6 overflow-y-auto">
      <div className="w-full max-w-2xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20">
            <Scissors size={18} className="text-red-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">YouTube Highlight Extractor</h2>
            <p className="text-xs text-white/40">
              AI wybierze najlepszy fragment 2–10 min z Twojego wideo
            </p>
          </div>
        </div>

        {/* URL Input */}
        <div className="space-y-2">
          <Label className="text-xs text-white/50">Link do wideo YouTube</Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Youtube
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-red-400/60"
              />
              <Input
                placeholder="https://youtube.com/watch?v=..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && viewState === "idle" && handleAnalyze()}
                className="pl-8 bg-white/[0.04] border-white/10 text-white placeholder:text-white/25 focus:border-white/20"
                disabled={viewState === "analyzing" || viewState === "downloading"}
              />
            </div>
            <Button
              onClick={
                viewState === "list" || viewState === "editing" || viewState === "error"
                  ? handleReset
                  : handleAnalyze
              }
              disabled={viewState === "analyzing" || viewState === "downloading" || !url.trim()}
              className="gap-2 bg-indigo-600 hover:bg-indigo-500 text-white border-0 shrink-0"
            >
              {viewState === "analyzing" ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Analizuję…
                </>
              ) : viewState === "list" || viewState === "editing" || viewState === "error" ? (
                <>
                  <RefreshCw size={14} /> Nowy film
                </>
              ) : (
                <>
                  <Sparkles size={14} /> Analizuj
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Analyzing loader */}
        {viewState === "analyzing" && (
          <div className="flex flex-col items-center gap-4 py-10 text-center">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-2 border-indigo-500/20 flex items-center justify-center">
                <Loader2 size={28} className="text-indigo-400 animate-spin" />
              </div>
            </div>
            <div>
              <p className="text-sm text-white/80 font-medium">AI analizuje wideo…</p>
              <p className="text-xs text-white/35 mt-1">
                Pobieranie transkryptu i wybieranie najlepszego fragmentu
              </p>
            </div>
          </div>
        )}

        {/* Error */}
        {viewState === "error" && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/8 border border-red-500/20">
            <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-300 font-medium">Nie udało się przeanalizować</p>
              <p className="text-xs text-red-300/60 mt-0.5">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* List of highlights */}
        {viewState === "list" && highlights.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-white/40">
                <Youtube size={12} className="text-red-400/60" />
                <span className="truncate">{videoTitle}</span>
              </div>
              <span className="text-xs text-white/40">
                Znaleziono {highlights.length}{" "}
                {highlights.length === 1 ? "highlight" : "highlightów"}
              </span>
            </div>

            <div className="space-y-2">
              {highlights.map((h, idx) => (
                <button
                  key={`${h.startSeconds}-${h.endSeconds}`}
                  onClick={() => handlePickHighlight(h)}
                  className="w-full text-left rounded-2xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/15 transition p-4 space-y-2"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs text-white/40">
                      <span className="font-mono">#{idx + 1}</span>
                      <Clock size={11} />
                      <span className="font-mono">
                        {formatSeconds(h.startSeconds)} → {formatSeconds(h.endSeconds)}
                      </span>
                      <span className="text-white/30">·</span>
                      <span>{durationLabel(h.startSeconds, h.endSeconds)}</span>
                    </div>
                    <Sparkles size={12} className="text-indigo-400/70" />
                  </div>
                  <p className="text-sm text-white font-medium leading-snug">{h.suggestedTitle}</p>
                  {h.reasoning && (
                    <p className="text-xs text-white/50 leading-relaxed">{h.reasoning}</p>
                  )}
                  {h.transcriptExcerpt && (
                    <p className="text-xs text-white/40 italic leading-relaxed">
                      „
                      {h.transcriptExcerpt.length > 100
                        ? h.transcriptExcerpt.slice(0, 100) + "…"
                        : h.transcriptExcerpt}
                      "
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Editing single highlight (also shown while downloading so spinner stays visible) */}
        {(viewState === "editing" || viewState === "downloading") && suggestion && (
          <div className="space-y-4">
            {/* Back link */}
            <button
              onClick={handleBackToListFromEdit}
              className="text-xs text-white/40 hover:text-white/70 transition flex items-center gap-1.5"
            >
              ← Wróć do listy ({highlights.length})
            </button>

            {/* Video title */}
            <div className="flex items-center gap-2 text-xs text-white/40">
              <Youtube size={12} className="text-red-400/60" />
              <span className="truncate">{suggestion.videoTitle}</span>
            </div>

            {/* Highlight card */}
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 space-y-5">
              {/* Timestamp range */}
              <div>
                <p className="text-xs text-white/40 mb-2 flex items-center gap-1.5">
                  <Clock size={11} />
                  Zaznaczony fragment
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 space-y-1">
                    <Label className="text-[10px] text-white/30">Od</Label>
                    <Input
                      value={editStart}
                      onChange={(e) => setEditStart(e.target.value)}
                      placeholder="0:00"
                      className="bg-white/[0.04] border-white/10 text-white text-sm font-mono focus:border-white/20"
                    />
                  </div>
                  <span className="text-white/30 mt-5">→</span>
                  <div className="flex-1 space-y-1">
                    <Label className="text-[10px] text-white/30">Do</Label>
                    <Input
                      value={editEnd}
                      onChange={(e) => setEditEnd(e.target.value)}
                      placeholder="10:00"
                      className="bg-white/[0.04] border-white/10 text-white text-sm font-mono focus:border-white/20"
                    />
                  </div>
                  <div className="mt-5 text-xs text-white/35 shrink-0">
                    {durationLabel(parseDurationInput(editStart), parseDurationInput(editEnd))}
                  </div>
                </div>
              </div>

              {/* Suggested title */}
              <div className="space-y-1">
                <Label className="text-xs text-white/40">Proponowany tytuł</Label>
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  maxLength={100}
                  className="bg-white/[0.04] border-white/10 text-white font-medium focus:border-indigo-500/40"
                />
              </div>

              {/* AI reasoning */}
              {suggestion.reasoning && (
                <div className="rounded-xl bg-indigo-500/5 border border-indigo-500/10 p-3">
                  <p className="text-[10px] text-indigo-400/60 uppercase tracking-wider mb-1">
                    Dlaczego ten fragment
                  </p>
                  <p className="text-xs text-white/55 leading-relaxed">{suggestion.reasoning}</p>
                </div>
              )}

              {/* Transcript excerpt */}
              {suggestion.transcriptExcerpt && (
                <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-3">
                  <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">
                    Początek fragmentu
                  </p>
                  <p className="text-xs text-white/50 italic leading-relaxed">
                    „
                    {showFullExcerpt || suggestion.transcriptExcerpt.length <= 150
                      ? suggestion.transcriptExcerpt
                      : suggestion.transcriptExcerpt.slice(0, 150) + "…"}
                    "
                  </p>
                  {suggestion.transcriptExcerpt.length > 150 && (
                    <button
                      type="button"
                      onClick={() => setShowFullExcerpt(!showFullExcerpt)}
                      className="text-[10px] text-indigo-400/60 hover:text-indigo-400 mt-1 transition-colors"
                    >
                      {showFullExcerpt ? "Zwiń" : "Rozwiń"}
                    </button>
                  )}
                </div>
              )}

              {/* YouTube embed preview — always visible in editing view */}
              {(() => {
                const videoId = extractVideoId(url);
                const startSec = parseDurationInput(editStart);
                const endSec = parseDurationInput(editEnd);
                const embedUrl = videoId
                  ? `https://www.youtube.com/embed/${videoId}?start=${startSec}&end=${endSec}&autoplay=0`
                  : null;
                const watchUrl = videoId
                  ? `https://www.youtube.com/watch?v=${videoId}&t=${startSec}`
                  : null;
                if (!embedUrl) return null;
                return (
                  <div className="space-y-3">
                    {downloadFailed && (
                      <div className="rounded-xl bg-amber-500/8 border border-amber-500/20 p-3">
                        <p className="text-xs text-amber-300/80">
                          Pobieranie na serwerze niedostępne — YouTube blokuje download z serwerów.
                          Obejrzyj fragment bezpośrednio:
                        </p>
                      </div>
                    )}
                    <div className="rounded-xl overflow-hidden border border-white/10">
                      <iframe
                        src={embedUrl}
                        className="w-full aspect-video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="YouTube highlight preview"
                      />
                    </div>
                    {watchUrl && (
                      <a
                        href={watchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full h-10 rounded-lg bg-red-600/20 hover:bg-red-600/30 border border-red-500/20 text-red-300 text-sm font-medium transition"
                      >
                        <ExternalLink size={14} />
                        Oglądaj na YouTube od {formatSeconds(startSec)}
                      </a>
                    )}
                  </div>
                );
              })()}

              {/* Download button */}
              {!downloadFailed && (
                <Button
                  onClick={handleDownload}
                  disabled={viewState === "downloading"}
                  className="w-full gap-2 bg-red-600 hover:bg-red-500 text-white border-0 h-10"
                >
                  {viewState === "downloading" ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Pobieranie klipu…
                    </>
                  ) : (
                    <>
                      <Download size={14} /> Pobierz highlight (.mp4)
                    </>
                  )}
                </Button>
              )}

              {/* Retry download button (shown after failure) */}
              {downloadFailed && (
                <Button
                  onClick={() => {
                    setDownloadFailed(false);
                    handleDownload();
                  }}
                  variant="outline"
                  className="w-full gap-2 border-white/10 text-white/60 hover:text-white hover:bg-white/5 h-9 text-xs"
                >
                  <RefreshCw size={12} /> Spróbuj pobrać ponownie
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
